import { Button, MarkdownPreview } from '@consigli/facade';
import { FC, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPencilAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { getMutationErrorMessage } from '../utils/mutation-error';

interface EditableSummarySectionProps {
  /** Fixed heading; never part of the editable text. */
  label: string;
  editLabel: string;
  /** Saved markdown shown while not editing. */
  content: string;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  /** Persist the draft; rejects to keep the editor open with the error shown. */
  onSave: () => Promise<unknown>;
  canSave: boolean;
  /** The editor, rendered while `isEditing`. */
  children: ReactNode;
}

export const EditableSummarySection: FC<EditableSummarySectionProps> = ({
  label,
  editLabel,
  content,
  isEditing,
  onEdit,
  onCancel,
  onSave,
  canSave,
  children,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  // Focus only when the user clicks Edit, not when an open draft remounts
  // after switching back to the Summary tab.
  const focusOnOpenRef = useRef(false);

  useEffect(() => {
    if (!isEditing || !focusOnOpenRef.current) return;
    focusOnOpenRef.current = false;
    editorRef.current?.querySelector<HTMLElement>('textarea, input')?.focus();
  }, [isEditing]);

  const handleEdit = () => {
    focusOnOpenRef.current = true;
    onEdit();
  };

  const handleCancel = () => {
    setError(null);
    onCancel();
  };

  const handleSave = async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      await onSave();
      toast.success(t('meeting-secretary.meetings.summary-updated'));
    } catch (err) {
      setError(
        getMutationErrorMessage(err, t('meeting-secretary.meetings.error-updating-summary')),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-gray-900">{label}</h3>
        {!isEditing && (
          <Button
            tertiary
            iconOnly
            icon={FaPencilAlt}
            iconSize={14}
            onClick={handleEdit}
            className="!p-2 rounded-full text-gray-400 hover:text-brand hover:bg-day-light-3 transition-colors"
            title={editLabel}
            aria-label={editLabel}
          />
        )}
      </div>

      {isEditing ? (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- only catches Escape and Cmd/Ctrl+Enter bubbling up from the editor's own inputs
        <div
          ref={editorRef}
          className="space-y-2"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              handleCancel();
            } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void handleSave();
            }
          }}
        >
          {children}
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button tertiary rounded onClick={handleCancel} disabled={isSaving}>
              {t('meeting-secretary.meetings.cancel')}
            </Button>
            <Button primary rounded onClick={handleSave} disabled={!canSave || isSaving}>
              {t('meeting-secretary.meetings.save')}
            </Button>
          </div>
        </div>
      ) : (
        <MarkdownPreview content={content} className="text-gray-700" />
      )}
    </section>
  );
};
